import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePolygonStore,
} from '@monopolio-ui/state-library'
import { EHeatMapCategory, ESearchLevel } from '@monopolio-ui/types-library'
import { useEffect, useMemo, useRef } from 'react'
import { tileZoomConfiguration } from '../../utils'
import {
  animateChangeHeatMapByCategories,
  animateHoverPolygon,
  filterPolygonByUrlCode,
  heatMapCaseExpressionColors,
  heatMapColors,
  presenterPolygonProperties,
} from '../../utils/heatMapUtils'
import PolygonPopup from './polygonPopup'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'

const MunicipalityPolygonManager = (): JSX.Element => {
  const {
    map,
    mapZoom,
    isFlyTo,
    isLoadedMap,
    drawingTool,
    hoveredLayer,
    setIsFlyTo,
  } = useMapStore()

  const {
    polygonTiles,
    heatMapCategory,
    isEnabledHeatMap,
    polygonNavigation,
    setPolygonNavigation,
    clearPolygonNavigation,
  } = usePolygonStore()

  const { searchLevel, setLatitude, setLongitude, setSearchLevel } =
    useFilterStore()

  const { pointsSvgContainer } = usePointStore()

  const heatMapCategoryPrevious = usePrevious<EHeatMapCategory | null>(
    heatMapCategory
  )

  const mapZoomPrevious = usePrevious<number>(mapZoom)

  const pointsSvgContainerRef = useRef<any>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const heatMapCategoryRef = useRef<EHeatMapCategory | null>(heatMapCategory)
  heatMapCategoryRef.current = heatMapCategory

  const municipalitiesTiles = useMemo(() => {
    const filter = polygonTiles.filter(
      (tile) => tile?.tiles?.municipalities?.id
    )
    return filter.map((tile) => tile.tiles.municipalities)
  }, [polygonTiles])

  const loadMunicipalityLevel = (params: {
    category: EHeatMapCategory
  }): void => {
    if (!map) {
      return
    }

    const { category } = params

    for (const { id } of municipalitiesTiles) {
      const layerName = `${id}-municipality-polygon`
      const source = `${id}-municipality-source`
      const sourceLayer = id

      const find = map?.getSource(source)

      if (find) {
        continue
      }

      const { minZoom, maxZoom } = tileZoomConfiguration.municipality

      map?.addSource(source, {
        type: 'vector',
        url: `mapbox://davpach.${id}`,
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: layerName,
        type: 'fill',
        source,
        'source-layer': sourceLayer,
        filter:
          searchLevel === ESearchLevel.STATE
            ? ['==', 'id', 0]
            : ['!=', 'id', 0],
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_color'],
            heatMapCaseExpressionColors[category],
          ],
          'fill-opacity': 0.4,
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: `${id}-municipality-border`,
        type: 'line',
        source,
        'source-layer': sourceLayer,
        filter:
          searchLevel === ESearchLevel.STATE
            ? ['==', 'id', 0]
            : ['!=', 'id', 0],
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            heatMapColors[category].hover,
            '#0368FE',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_width'],
            1.678,
          ],
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      filterMunicipalityPolygonsByParent()

      createHoverEvent({ layerName, source, sourceLayer })
      createClickEvent({ layerName })
    }
  }

  const createHoverEvent = (params: {
    layerName: string
    source: string
    sourceLayer: string
  }) => {
    const { layerName, source, sourceLayer } = params

    let featureId: number | undefined

    map?.on('mousemove', layerName, ({ features }) => {
      if (!features?.length) {
        return
      }

      const feature = features[0]

      if (featureId !== undefined && feature.id !== featureId) {
        if (
          !featureId ||
          (drawingToolRef.current.isActive &&
            drawingToolRef.current.status !== 'processed') ||
          hoveredLayerRef?.current?.layerId
        ) {
          return
        }

        map?.setFeatureState(
          {
            source,
            sourceLayer,
            id: featureId,
          },
          { hover: false, hover_width: 1.678 }
        )
        featureId = undefined
        return
      }

      const { id } = feature

      if (featureId && featureId === id) {
        return
      }

      featureId = id as number

      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      setTimeout(() => {
        pointsSvgContainerRef?.current?.style('cursor', 'pointer')
        map.getCanvas().style.cursor = 'pointer'
      }, 50)

      let indexKey = 'price_index_rank'
      if (heatMapCategoryRef?.current === EHeatMapCategory.RENT) {
        indexKey = 'rent_index_rank'
      } else if (heatMapCategoryRef?.current === EHeatMapCategory.CAPRATE) {
        indexKey = 'caprate_index_rank'
      }

      const codeLevel = feature?.properties?.[indexKey]
      const heatMapColor = (heatMapCategoryRef?.current ?? heatMapCategory)as keyof (typeof heatMapColors)

      const bgColor =
        heatMapColors[heatMapColor][
          codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
        ] ?? '#ffffff'

      animateHoverPolygon({
        source,
        sourceLayer,
        featureId,
        previousBorder: 1.678,
        nextBorder: 4,
        bgColor,
        bgHoverColor:
          heatMapColors[heatMapColor].hover,
        duration: 300,
        map,
      })

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        {
          hover: true,
          hover_color:
            heatMapColors[heatMapColor][
              codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
            ],
          hover_width: 1.678,
        }
      )
    })

    map?.on('mouseleave', layerName, () => {
      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed')
      ) {
        return
      }

      pointsSvgContainerRef?.current?.style('cursor', 'grab')
      map.getCanvas().style.cursor = 'grab'

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        { hover: false, hover_width: 1.678 }
      )

      featureId = undefined
    })
  }

  const createClickEvent = (params: { layerName: string }) => {
    const { layerName } = params

    map?.on('click', layerName, (event) => {
      if (
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      map?.flyTo({
        center: event.lngLat,
        essential: true,
        speed: 1.4,
        curve: 1.3,
        duration: 1000,
        zoom: tileZoomConfiguration.municipality.maxZoom + 0.1,
      })

      setIsFlyTo(true)

      setTimeout(() => {
        setIsFlyTo(false)
      }, 1000)

      if (!event?.features?.length) {
        return
      }

      clearPolygonNavigation({
        municipality: true,
        neighborhood: true,
      })

      const properties = presenterPolygonProperties({
        properties: event?.features[0]?.properties,
      })

      setPolygonNavigation({
        level: ESearchLevel.MUNICIPALITY,
        properties,
      })

      setSearchLevel(ESearchLevel.MUNICIPALITY)
      setLatitude(properties?.centroid?.lat ?? 0)
      setLongitude(properties?.centroid?.lng ?? 0)
    })
  }

  const removeMunicipalityLevel = (): void => {
    if (!map) {
      return
    }

    for (const { id } of municipalitiesTiles) {
      const find = map?.getSource(`${id}-municipality-source`)

      if (!find) {
        continue
      }

      const layerName = `${id}-municipality-polygon`
      const source = `${id}-municipality-source`

      map.removeLayer(layerName)
      map.removeLayer(`${id}-municipality-border`)
      map.removeSource(source)
    }
  }

  const changeMunicipalityLevelHeatMapColor = (params: {
    category: EHeatMapCategory
    previousCategory: EHeatMapCategory | null
  }): void => {
    if (!map) {
      return
    }

    const { category, previousCategory } = params

    for (const { id } of municipalitiesTiles) {
      const find = map?.getSource(`${id}-municipality-source`)

      if (!find) {
        continue
      }

      animateChangeHeatMapByCategories({
        layerName: `${id}-municipality-polygon`,
        previousCategory: previousCategory || category,
        nextCategory: category,
        duration: 300,
        map,
      })

      map.setPaintProperty(`${id}-municipality-border`, 'line-color', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        heatMapColors[category].hover,
        '#0368FE',
      ])
    }
  }

  const filterMunicipalityPolygonsByParent = (): void => {
    if (!map || !isLoadedMap || !polygonTiles?.length || !isEnabledHeatMap) {
      return
    }

    const { state } = polygonNavigation as {state:{
      fullUrlCode: string
    }}

    if (!state) {
      resetFilterMunicipalityPolygons()
      return
    }

    for (const { id } of municipalitiesTiles) {
      if (!id) {
        continue
      }

      filterPolygonByUrlCode({
        map,
        layers: [`${id}-municipality-polygon`, `${id}-municipality-border`],
        urlCode: state?.fullUrlCode,
      })
    }
  }

  const resetFilterMunicipalityPolygons = (): void => {
    if (!map || !polygonTiles?.length || !isLoadedMap || !isEnabledHeatMap) {
      return
    }

    for (const { id } of municipalitiesTiles) {
      const mapLayer = map.getLayer(`${id}-municipality-polygon`)
      if (!id || !mapLayer) {
        continue
      }
      map.setFilter(`${id}-municipality-polygon`, null)
      map.setFilter(`${id}-municipality-border`, null)
    }
  }

  useEffect(() => {
    if (map && isLoadedMap && municipalitiesTiles?.length) {
      if (isEnabledHeatMap) {
        loadMunicipalityLevel({ category: heatMapCategory })
      } else {
        removeMunicipalityLevel()
      }
    }
  }, [map, isLoadedMap, municipalitiesTiles, isEnabledHeatMap])

  useEffect(() => {
    if (heatMapCategory && isEnabledHeatMap && map) {
      changeMunicipalityLevelHeatMapColor({
        category: heatMapCategory,
        previousCategory: heatMapCategoryPrevious,
      })
    }
  }, [heatMapCategory])

  useEffect(filterMunicipalityPolygonsByParent, [
    (polygonNavigation as { state:string })?.state,
    map,
    isLoadedMap,
    polygonTiles,
    isEnabledHeatMap,
  ])

  useEffect(() => {
    if (!map || !isLoadedMap || !polygonTiles?.length) {
      return
    }
    if (
      !mapZoomPrevious ||
      mapZoomPrevious < mapZoom ||
      mapZoom > tileZoomConfiguration.municipality.minZoom ||
      tileZoomConfiguration.municipality.minZoom - mapZoom < 0.5 ||
      isFlyTo
    ) {
      return
    }

    setPolygonNavigation({
      level: ESearchLevel.MUNICIPALITY,
      properties: undefined,
    })
    setPolygonNavigation({
      level: ESearchLevel.NEIGHBORHOOD,
      properties: undefined,
    })
    setPolygonNavigation({
      level: ESearchLevel.BlOCK,
      properties: undefined,
    })
  }, [mapZoom])

  return (
    <>
      {municipalitiesTiles.map((state) => (
        <PolygonPopup
          key={state.id}
          layer={`${state.id}-municipality-polygon`}
          level="municipality"
        />
      ))}
    </>
  )
}

export default MunicipalityPolygonManager
